<template>
  <table class="BookingRecapSummary">
    <ai-typo
      as="caption"
      variant="heading-04"
      color="gold-900"
      class="BookingRecapSummary-title">
      {{ $t('booking.recap.summary.title') }}
    </ai-typo>

    <tbody>
      <!-- Dates -->
      <tr class="BookingRecapSummary-row">
        <td class="BookingRecapSummary-icon">
          <ai-icon
            name="calendar"
            :size="24"
            :aria-label="$t('booking.recap.icons.dates')" />
        </td>
        <td>
          <ai-typo as="span" variant="paragraph-04-bold">
            {{ dates }}
          </ai-typo>
        </td>
      </tr>

      <!-- Rooms & guests -->
      <tr class="BookingRecapSummary-row">
        <td class="BookingRecapSummary-icon">
          <ai-icon
            name="guest"
            :size="24"
            :aria-label="$t('booking.recap.icons.guestsInfos')" />
        </td>
        <td>
          <ai-typo as="span" variant="paragraph-04-bold">
            {{ roomAndGuests }}
          </ai-typo>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { format } from 'date-fns';

import { useBookingStore } from '../../store';

const bookingStore = useBookingStore();

const { t } = useI18n();

const dateFnsLocale = useDateFnsLocale();

let lastComputedDates: string = '';
let lastComputedNRooms: string = '';

const dates = computed(() => {
  if (!bookingStore.isoDateIn) return lastComputedDates;

  const nights = bookingStore.nights;
  const beginDate = new Date(bookingStore.isoDateIn as string);
  const endDate = new Date(bookingStore.isoDateOut as string);

  lastComputedDates = t('booking.recap.summary.dates', nights, {
    named: {
      checkInDate: format(beginDate, 'dd LLL', { locale: dateFnsLocale.value }),
      checkOutDate: format(endDate, 'dd LLL', { locale: dateFnsLocale.value }),
      nights,
    },
  });

  return lastComputedDates;
});

const roomAndGuests = computed(() => {
  const nRooms: number | undefined = bookingStore.numberOfRooms;
  const nGuests: number | undefined = bookingStore.totalGuestsCount;

  // those values are going from vuex, so can be null or undefined
  if (
    typeof nRooms !== 'number' ||
    typeof nGuests !== 'number' ||
    nRooms === 0 ||
    nGuests === 0
  ) {
    return lastComputedNRooms;
  }

  lastComputedNRooms = t('booking.recap.summary.roomsAndGuests', {
    rooms: t('booking.recap.summary.rooms', { count: nRooms }),
    guests: t('booking.recap.summary.guests', { count: nGuests }),
  });
  return lastComputedNRooms;
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';

.BookingRecapSummary-row {
  display: flex;
  flex-direction: row;
  align-items: center;

  &:last-of-type {
    margin-bottom: 0;
  }

  &--spaceBetween {
    justify-content: space-between;
  }
}

.BookingRecapSummary-title {
  text-align: left;
  margin-bottom: constants.$inner-02;
}

.BookingRecapSummary-icon {
  color: colors.$gold-700;
  margin-right: constants.$inner-02;
}
</style>
